import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardMedia,
  CardContent,
  Grid,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { getCourses, addCourse, deleteCourse } from "../services/api";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://api.hunarmandsindh.pk/api";
const UPLOAD_BASE_URL = API_BASE_URL.replace("/api", "");

interface Course {
  _id: string;
  name: string;
  image: string;
  price: number;
  description: string;
  isActive: boolean;
}

const Courses: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    price: 1000,
    description: "",
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await getCourses();
      setCourses(response.data.data);
      setError("");
    } catch (err: any) {
      setError("Failed to fetch courses. Please check if the backend is running.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFormData({ name: "", price: 1000, description: "" });
    setImageFile(null);
    setImagePreview(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!imageFile) {
      setSnackbar({ open: true, message: "Please select an image", severity: "error" });
      return;
    }

    try {
      setSubmitting(true);
      const data = new FormData();
      data.append("name", formData.name);
      data.append("price", formData.price.toString());
      data.append("description", formData.description);
      data.append("image", imageFile);

      await addCourse(data);
      setSnackbar({ open: true, message: "Course added successfully", severity: "success" });
      handleClose();
      fetchCourses();
    } catch (err: any) {
      setSnackbar({ open: true, message: "Failed to add course", severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this course?")) {
      try {
        await deleteCourse(id);
        setSnackbar({ open: true, message: "Course deleted successfully", severity: "success" });
        fetchCourses();
      } catch (err: any) {
        setSnackbar({ open: true, message: "Failed to delete course", severity: "error" });
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="800" color="#1e293b">
            Manage Courses
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add or remove courses from the official website.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchCourses}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpen}
            sx={{
              bgcolor: '#059669',
              '&:hover': { bgcolor: '#047857' },
              borderRadius: '12px',
              px: 3,
            }}
          >
            Add New Course
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {courses.length === 0 ? (
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 8, textAlign: 'center', borderRadius: '24px', bgcolor: '#f8fafc' }}>
                <Typography color="text.secondary">No courses found. Add your first course!</Typography>
              </Paper>
            </Grid>
          ) : (
            courses.map((course) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={course._id}>
                <Card sx={{ 
                  borderRadius: '24px', 
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  overflow: 'hidden',
                  transition: 'transform 0.3s',
                  '&:hover': { transform: 'translateY(-5px)' }
                }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={`${UPLOAD_BASE_URL}/${course.image}`}
                    alt={course.name}
                    sx={{ bgcolor: '#f1f5f9' }}
                  />
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight="700" noWrap gutterBottom>
                      {course.name}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <IconButton 
                        onClick={() => handleDelete(course._id)}
                        color="error"
                        sx={{ bgcolor: '#fef2f2', '&:hover': { bgcolor: '#fee2e2' } }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* Add Course Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
        <form onSubmit={handleSubmit}>
          <DialogTitle sx={{ fontWeight: 800 }}>Add New Course</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Course Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
              
              <Box>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="course-image-upload"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="course-image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    startIcon={<UploadIcon />}
                    sx={{ py: 2, borderStyle: 'dashed', borderRadius: '12px' }}
                  >
                    {imageFile ? imageFile.name : "Upload Course Banner Image"}
                  </Button>
                </label>
              </Box>

              {imagePreview && (
                <Box sx={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                  <img src={imagePreview} alt="Preview" style={{ width: '100%', height: 'auto', display: 'block' }} />
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleClose} color="inherit">Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={submitting}
              sx={{ 
                bgcolor: '#059669', 
                '&:hover': { bgcolor: '#047857' },
                borderRadius: '12px',
                px: 4
              }}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : "Add Course"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Courses;
